import { useState, useEffect, useRef } from "react"
import { Link, useNavigate, useLocation } from "@tanstack/react-router"
import { Menu, User, LayoutDashboard, LogOut } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetClose,
} from "@/components/ui/sheet"
import { useUIStore } from "@/store/ui.store"
import { useAuthStore } from "@/store/auth.store"
import { cn } from "@/lib/utils"
import { Tooltip } from "@/components/ui/Tooltip"
import { signOut } from "@/lib/api/login.api"

function ProfileMenu({
  user,
  navLinks,
  onScrollTo,
  onNavigateDashboard,
  onSignOut,
}: {
  user: any
  navLinks: Array<{ label: string; to?: string; target?: string }>
  onScrollTo: (target: string) => void
  onNavigateDashboard: () => void
  onSignOut: () => void
}) {
  const [profileOpen, setProfileOpen] = useState(false)
  const profileRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) {
        setProfileOpen(false)
      }
    }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [])

  return (
    <div className="relative" ref={profileRef}>
      <Tooltip content="Profile Menu" position="bottom" disabled={profileOpen}>
        <button
          type="button"
          onClick={() => setProfileOpen(!profileOpen)}
          className={cn(
            "flex items-center justify-center size-8 rounded-lg transition-colors cursor-pointer",
            profileOpen
              ? "bg-primary/10 text-primary"
              : "text-slate-700 hover:bg-slate-100 hover:text-slate-900"
          )}
          aria-label="Profile Menu"
        >
          <User className="size-5 shrink-0" />
        </button>
      </Tooltip>

      {profileOpen && (
        <div className="absolute right-0 top-full mt-2 w-52 bg-white border border-slate-200 rounded-xl shadow-lg z-50 overflow-hidden py-1">
          {/* User info */}
          <div className="px-4 py-3 border-b border-slate-100">
            <p className="text-[13px] font-bold text-slate-800 truncate">{user?.name || "User"}</p>
            <p className="text-[11px] text-slate-400 truncate">{user?.email || ""}</p>
          </div>

          {/* Navigation links for mobile and screens smaller than lg */}
          <div className="lg:hidden border-b border-slate-100 py-1">
            {navLinks.map((link) => (
              <button
                key={link.label}
                type="button"
                onClick={() => {
                  setProfileOpen(false)
                  if (link.label === "Home" && link.target) {
                    onScrollTo(link.target)
                  } else if (link.to) {
                    navigate({ to: link.to as any })
                  } else if (link.target) {
                    onScrollTo(link.target)
                  }
                }}
                className="w-full flex items-center px-4 py-2.5 text-[13px] font-medium text-slate-600 hover:bg-slate-50 hover:text-slate-800 transition-colors cursor-pointer text-left"
              >
                <span>{link.label}</span>
              </button>
            ))}
          </div>

          {/* Dashboard link */}
          <button
            type="button"
            onClick={() => {
              setProfileOpen(false)
              onNavigateDashboard()
            }}
            className="w-full flex items-center gap-2 px-4 py-2.5 text-[13px] font-medium text-slate-600 hover:bg-slate-50 hover:text-slate-800 transition-colors cursor-pointer"
          >
            <LayoutDashboard className="size-4 text-slate-400 shrink-0" />
            <span>Dashboard</span>
          </button>

          <div className="border-t border-slate-100 my-1" />

          {/* Sign Out */}
          <button
            type="button"
            onClick={() => {
              setProfileOpen(false)
              onSignOut()
            }}
            className="w-full flex items-center gap-2 px-4 py-2.5 text-[13px] font-medium text-slate-600 hover:bg-slate-50 hover:text-slate-800 transition-colors cursor-pointer"
          >
            <LogOut className="size-4 text-slate-400 shrink-0" />
            <span>Sign Out</span>
          </button>
        </div>
      )}
    </div>
  )
}

export function Navbar() {
  const { isMobileMenuOpen, setMobileMenuOpen } = useUIStore()
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const user = useAuthStore((s) => s.user)
  const { clearAuth, accessToken, refreshToken } = useAuthStore()
  const navigate = useNavigate()
  const location = useLocation()
  const isHomePage = location.pathname === "/"

  const [isScrolled, setIsScrolled] = useState(false)
  const [activeSection, setActiveSection] = useState<string>("hero")

  const handleSignOut = async () => {
    await signOut(accessToken ?? "", refreshToken ?? "")
    clearAuth()
    navigate({ to: "/" })
  }

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY || document.documentElement.scrollTop
      setIsScrolled(scrollY > 12)

      if (isHomePage) {
        const sections = [
          { id: "hero", el: document.querySelector("#hero") || document.querySelector("#top") },
          { id: "live-command-center", el: document.querySelector("#live-command-center") },
          { id: "pan-india-network", el: document.querySelector("#pan-india-network") },
          { id: "how-it-works", el: document.querySelector("#how-it-works") || document.querySelector("#how-it-works-tilted") },
          { id: "supplier-steps", el: document.querySelector("#supplier-steps") },
        ]

        const headerHeight = 64
        let current = "hero"
        for (const section of sections) {
          if (section.el) {
            const top = section.el.getBoundingClientRect().top + scrollY - headerHeight - 120
            if (scrollY >= top) {
              current = section.id
            }
          }
        }
        setActiveSection(current)
      }
    }

    window.addEventListener("scroll", handleScroll, { passive: true })
    handleScroll()
    return () => window.removeEventListener("scroll", handleScroll)
  }, [isHomePage])

  const handleScrollTo = (id: string) => {
    const scrollToTarget = (targetId: string) => {
      if (targetId === "#hero" || targetId === "#top") {
        window.scrollTo({ top: 0, behavior: "smooth" })
        return
      }
      const el =
        document.querySelector(targetId) ||
        (targetId === "#how-it-works" ? document.querySelector("#how-it-works-tilted") : null)
      if (el) {
        const header = document.querySelector("header")
        const headerHeight = header ? header.getBoundingClientRect().height : 48
        const style = window.getComputedStyle(el)
        const scrollMarginTop = parseFloat(style.scrollMarginTop) || 0
        const defaultOffset = targetId === "#live-command-center" ? headerHeight : headerHeight + 16
        const offset = scrollMarginTop > 0 ? scrollMarginTop : defaultOffset
        const elPosition = el.getBoundingClientRect().top + window.scrollY
        window.scrollTo({
          top: Math.max(0, elPosition - offset),
          behavior: "smooth",
        })
      }
    }

    if (isHomePage) {
      scrollToTarget(id)
    } else {
      navigate({ to: "/" })
      const tryScroll = (retries = 4) => {
        const el = document.querySelector(id)
        if (el || id === "#hero" || id === "#top") {
          scrollToTarget(id)
        } else if (retries > 0) {
          setTimeout(() => tryScroll(retries - 1), 150)
        }
      }
      setTimeout(() => tryScroll(), 200)
    }
  }

  const NAV_LINKS = [
    ...(!isHomePage ? [{ label: "Home", to: "/" }] : []),
    { label: "Explore", to: "/explore" },
    { label: "Live Trade", target: "#live-command-center" },
    { label: "Global Corridor", target: "#pan-india-network" },
    { label: "How It Works", target: "#how-it-works" },
    { label: "Start Selling", target: "#supplier-steps" },
  ]

  const isSectionActive = (link: (typeof NAV_LINKS)[number]) => {
    if (!isHomePage) {
      return link.to ? location.pathname === link.to : false
    }
    if (link.to && link.to !== "/") {
      return location.pathname.startsWith(link.to)
    }
    if (link.label === "Home") return activeSection === "hero"
    if (link.target === "#live-command-center") return activeSection === "live-command-center"
    if (link.target === "#pan-india-network") return activeSection === "pan-india-network"
    if (link.target === "#how-it-works") return activeSection === "how-it-works"
    if (link.target === "#supplier-steps") return activeSection === "supplier-steps"
    return false
  }

  return (
    <header
      className={cn(
        "fixed top-0 left-0 right-0 z-50 w-full transition-all duration-300 ease-in-out",
        isScrolled
          ? "bg-white/90 backdrop-blur-md border-b border-slate-200/80 shadow-xs shadow-slate-900/[0.04]"
          : "bg-transparent border-b border-transparent shadow-none"
      )}
    >
      <div className="flex h-12 w-full items-center justify-between px-3 sm:px-4 lg:px-5">
        {/* Left — Brand Logo & Navigation Links (aligned together on left, same as BuyerTopBar) */}
        <div className="flex items-center gap-12 xl:gap-20 shrink-0">
          <Link
            to="/"
            className="group shrink-0 flex items-center gap-1 font-sans text-xl font-black tracking-tight text-slate-900 select-none cursor-pointer transition-all duration-300"
            title="Go to Home"
          >
            <span className="text-primary transition-transform duration-300 group-hover:scale-110">E</span>
            <span>NGA</span>
          </Link>

          {/* Navigation Links */}
          <nav className="hidden lg:flex items-center gap-4 xl:gap-6">
            {NAV_LINKS.map((link) => {
              const active = isSectionActive(link)
              return link.to ? (
                <Link
                  key={link.label}
                  to={link.to}
                  className={cn(
                    "text-[13px] font-semibold transition-colors cursor-pointer select-none",
                    active ? "text-slate-950 font-bold" : "text-slate-600 hover:text-slate-950"
                  )}
                >
                  {link.label}
                </Link>
              ) : (
                <button
                  key={link.label}
                  type="button"
                  onClick={() => handleScrollTo(link.target!)}
                  className={cn(
                    "text-[13px] font-semibold transition-colors cursor-pointer select-none",
                    active ? "text-slate-950 font-bold" : "text-slate-600 hover:text-slate-950"
                  )}
                >
                  {link.label}
                </button>
              )
            })}
          </nav>
        </div>

        {/* Right — Actions & Responsive Navigation */}
        <div className="flex items-center gap-2">
          {isAuthenticated ? (
            <ProfileMenu
              user={user}
              navLinks={NAV_LINKS}
              onScrollTo={handleScrollTo}
              onNavigateDashboard={() => navigate({ to: "/dashboard" })}
              onSignOut={handleSignOut}
            />
          ) : (
            <Button
              className="hidden lg:flex h-7.5 px-4 rounded-full bg-primary hover:bg-primary-hover text-white text-xs font-bold transition-all shadow-xs items-center gap-1.5 cursor-pointer active:scale-95"
              asChild
            >
              <Link to="/login">
                <span>Sign In</span>
              </Link>
            </Button>
          )}

          {/* Hamburger Menu & Sheet (accessible on all screens where desktop nav is hidden: < lg) */}
          <div className="lg:hidden flex items-center">
            <Sheet open={isMobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <button
                  aria-label="Open menu"
                  className="flex items-center justify-center rounded-lg p-1.5 text-slate-700 hover:text-primary hover:bg-slate-100 transition-colors cursor-pointer"
                >
                  <Menu className="size-5" />
                </button>
              </SheetTrigger>

            <SheetContent side="right" className="bg-white border-l border-slate-200 flex flex-col justify-between p-6">
              <div>
                <SheetHeader>
                  <SheetTitle>
                    <span className="font-sans text-2xl font-black tracking-tight text-slate-900 select-none">
                      <span className="text-primary">E</span>NGA
                    </span>
                  </SheetTitle>
                </SheetHeader>

                <nav className="mt-8 flex flex-col gap-1.5">
                  {NAV_LINKS.map((link) => {
                    const active = isSectionActive(link)
                    return (
                      <SheetClose key={link.label} asChild>
                        {link.to ? (
                          <Link
                            to={link.to}
                            className={cn(
                              "flex items-center rounded-xl px-4 py-2.5 text-sm font-semibold transition-all text-left w-full cursor-pointer",
                              active
                                ? "bg-slate-100 text-slate-950 font-bold"
                                : "text-slate-800 hover:bg-slate-50"
                            )}
                          >
                            {link.label}
                          </Link>
                        ) : (
                          <button
                            type="button"
                            onClick={() => handleScrollTo(link.target!)}
                            className={cn(
                              "flex items-center rounded-xl px-4 py-2.5 text-sm font-semibold transition-all text-left w-full cursor-pointer",
                              active
                                ? "bg-slate-100 text-slate-950 font-bold"
                                : "text-slate-800 hover:bg-slate-50"
                            )}
                          >
                            {link.label}
                          </button>
                        )}
                      </SheetClose>
                    )
                  })}
                </nav>
              </div>

              {isAuthenticated ? (
                <div className="pt-4 border-t border-slate-100 flex flex-col gap-2">
                  <div className="px-2 py-1">
                    <p className="text-sm font-bold text-slate-800 truncate">{user?.name || "User"}</p>
                    <p className="text-xs text-slate-400 truncate">{user?.email || ""}</p>
                  </div>
                  <SheetClose asChild>
                    <button
                      type="button"
                      onClick={() => navigate({ to: "/dashboard" })}
                      className="flex items-center gap-2.5 rounded-xl px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors w-full text-left cursor-pointer"
                    >
                      <LayoutDashboard className="size-4 text-slate-400 shrink-0" />
                      <span>Dashboard</span>
                    </button>
                  </SheetClose>
                  <SheetClose asChild>
                    <button
                      type="button"
                      onClick={handleSignOut}
                      className="flex items-center gap-2.5 rounded-xl px-3 py-2 text-sm font-semibold text-rose-600 hover:bg-rose-50 transition-colors w-full text-left cursor-pointer"
                    >
                      <LogOut className="size-4 text-rose-500 shrink-0" />
                      <span>Sign Out</span>
                    </button>
                  </SheetClose>
                </div>
              ) : (
                <div className="pt-6 border-t border-slate-100">
                  <SheetClose asChild>
                    <Button className="w-full rounded-full bg-primary hover:bg-primary-hover text-white font-bold py-2.5 flex items-center justify-center gap-2" asChild>
                      <Link to="/login">
                        <span>Sign In</span>
                      </Link>
                    </Button>
                  </SheetClose>
                </div>
              )}
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </div>
  </header>
  )
}
